import { useEffect, useMemo, useRef, useState } from "react";

import { Button, Input, makeStyles, Spinner, tokens, Tooltip } from "@fluentui/react-components";
import {
  ArrowRight16Regular,
  Open16Regular,
  Search16Regular,
  Target16Regular,
  ThumbDislike16Regular,
  ThumbLike16Regular,
} from "@fluentui/react-icons";
import { useLocation, useNavigate } from "react-router-dom";

import { searchAssistant, sendAssistantFeedback, type AssistantResultCard } from "./ask-api";
import type { AssistantSearchResponse } from "./ask-api";
import { flashAnchor, type MissReason, type YieldHandler } from "./dom";
import type { HelpNotice } from "./HelpLauncher";
import { matchesRoute } from "./registry";
import type { HelpLanguage } from "./types";
import { uiText, type HelpUiText } from "./ui";

/**
 * "Ask" — the question half of the manual: "where do I do X?", answered for ANY page from the
 * catalog under `frontend-employee/catalog/`.
 *
 * The panel renders reviewed entries verbatim; nothing here composes an answer, so a bad answer
 * is a content bug in the catalog, not a model to tune. Three behaviours are load-bearing:
 *   - "Not found" is shown as itself, with the nearest jobs offered only as searches to try.
 *   - "Take me there" navigates AND flashes the exact control; an entry whose job starts in a
 *     dialog (`anchor_action: open`) opens it.
 *   - It only offers to point at a control the destination page renders (`anchorRoute`), and says
 *     the missing step out loud when it cannot.
 */

/** Shorter than this is a word in the making, not a question — it would only pollute the miss log. */
const MIN_QUESTION = 5;
/** Search-as-you-type, half a second after the last keystroke. */
const DEBOUNCE_MS = 500;

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", minHeight: 0, flex: 1 },
  top: { padding: "16px 20px 12px", flexShrink: 0 },
  input: { width: "100%" },
  hint: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForegroundDisabled,
    marginTop: "6px",
    lineHeight: 1.5,
  },
  results: { flex: 1, minHeight: 0, overflowY: "auto", padding: "0 20px 24px" },
  muted: { fontSize: "12.5px", color: tokens.colorNeutralForegroundDisabled, marginTop: "8px" },
  card: {
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "12px",
    padding: "14px",
    marginBottom: "10px",
    backgroundColor: tokens.colorNeutralBackground1,
  },
  chipRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "6px",
    marginBottom: "4px",
  },
  chip: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: "999px",
    padding: "1px 6px",
    whiteSpace: "nowrap",
  },
  fallback: {
    fontSize: "10.5px",
    color: tokens.colorNeutralForegroundDisabled,
    fontStyle: "italic",
  },
  job: { fontSize: "14px", fontWeight: tokens.fontWeightBold, lineHeight: 1.3 },
  description: {
    fontSize: "12.5px",
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
    lineHeight: 1.6,
  },
  needs: {
    fontSize: "11.5px",
    color: tokens.colorPaletteDarkOrangeForeground1,
    marginTop: "6px",
    lineHeight: 1.5,
    fontStyle: "italic",
  },
  offPage: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
    lineHeight: 1.5,
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginTop: "10px",
    minHeight: "30px",
  },
  id: { fontSize: "11px", color: tokens.colorNeutralForegroundDisabled, marginLeft: "auto" },
  notFound: { marginTop: "8px" },
  notFoundText: { fontSize: "12.5px", color: tokens.colorNeutralForeground2, lineHeight: 1.6 },
  tryThese: {
    fontSize: "11.5px",
    color: tokens.colorNeutralForegroundDisabled,
    marginTop: "12px",
    marginBottom: "6px",
  },
  suggestions: { display: "flex", flexWrap: "wrap", gap: "6px" },
  feedback: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    marginTop: "8px",
    paddingTop: "10px",
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: "11.5px",
    color: tokens.colorNeutralForegroundDisabled,
  },
  chatbot: { fontSize: "11.5px", color: tokens.colorNeutralForegroundDisabled, marginTop: "16px" },
});

/**
 * Can the panel point at this card's control — here, or on the page it is about to open? Shared by
 * the card (which labels the button) and the handler (which starts the flash), so the two never
 * disagree about whether there is anything to flash.
 */
const canPointAt = (card: AssistantResultCard, pathname: string): boolean =>
  card.anchor !== null &&
  (card.anchorRoute === card.deeplink || matchesRoute(card.anchorRoute, pathname));

const ResultCard = ({
  card,
  text,
  onNavigate,
}: {
  card: AssistantResultCard;
  text: HelpUiText;
  onNavigate: (card: AssistantResultCard) => void;
}) => {
  const styles = useStyles();
  const { pathname } = useLocation();
  const onThisScreen = pathname === card.deeplink;
  const opens = card.anchor !== null && card.anchorAction === "open";
  const offPage = card.anchor !== null && !canPointAt(card, pathname);
  const pointHere = card.anchor !== null && matchesRoute(card.anchorRoute, pathname);

  return (
    <div className={styles.card}>
      <div className={styles.chipRow}>
        <span className={styles.chip}>{card.area}</span>
        {card.fallback && <span className={styles.fallback}>{text.askInEnglish}</span>}
      </div>

      <div className={styles.job}>{card.job}</div>
      <div className={styles.description}>{card.description}</div>

      {card.prerequisites && (
        <div className={styles.needs}>
          {text.askNeeds}: {card.prerequisites}
        </div>
      )}

      {offPage && <div className={styles.offPage}>{text.askOffPage}</div>}

      <div className={styles.actions}>
        {pointHere ? (
          <Button
            size="small"
            appearance="outline"
            icon={opens ? <Open16Regular /> : <Target16Regular />}
            onClick={() => onNavigate(card)}
          >
            {opens ? text.askOpenIt : text.askShowMe}
          </Button>
        ) : onThisScreen && offPage ? null : (
          <Button
            size="small"
            appearance="outline"
            icon={<ArrowRight16Regular />}
            iconPosition="after"
            onClick={() => onNavigate(card)}
          >
            {opens ? text.askTakeMeThereOpen : text.askTakeMeThere}
          </Button>
        )}
        <span className={styles.id}>{card.id}</span>
      </div>
    </div>
  );
};

interface HelpAskProps {
  language: HelpLanguage;
  /** Closes the drawer when the panel navigates away or opens a form. */
  onNavigated: () => void;
  /** Asks the drawer to stand aside while a control on THIS page is being pointed at. */
  onYield?: YieldHandler;
  /**
   * Where a miss is reported — a toast, because pointing at a control on ANOTHER page closes the
   * drawer, so by the time the poll gives up there is no card left to write on.
   */
  notify: (notice: HelpNotice) => void;
}

export const HelpAsk = ({ language, onNavigated, onYield, notify }: HelpAskProps) => {
  const styles = useStyles();
  const text = uiText(language);
  const [question, setQuestion] = useState("");
  const [debounced, setDebounced] = useState("");
  const [data, setData] = useState<AssistantSearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Deliberately NOT cancelled on unmount: navigating closes the drawer, which unmounts this panel
  // a tick after the flash is requested — cancelling there swallowed the flash. A superseding
  // flash cancels the previous one.
  const cancelFlash = useRef<(() => void) | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(question.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [question]);

  const enabled = debounced.length >= MIN_QUESTION;

  /**
   * Answered in the page, so there is no request to cache — and deliberately no cache either: a
   * repeated MISS must be logged each time it is asked, or the miss log undercounts exactly the
   * questions it exists to surface.
   */
  useEffect(() => {
    setFeedbackSent(false);
    if (!enabled) {
      setData(null);
      return;
    }
    let current = true;
    setSearching(true);
    searchAssistant({ question: debounced, screen: pathname, language, limit: 4 })
      .then((response) => {
        if (current) setData(response);
      })
      .catch((error: unknown) => {
        // A validation failure is a programming error in this panel, never the user's.
        console.error(error);
        if (current) setData(null);
      })
      .finally(() => {
        if (current) setSearching(false);
      });
    return () => {
      current = false;
    };
  }, [debounced, enabled, language, pathname]);

  const cards = useMemo(() => data?.answer.entries ?? [], [data]);

  /** Says out loud that the control was not pointed at — a silent miss reads as a broken button. */
  const reportMiss = (card: AssistantResultCard, reason: MissReason): void => {
    const line = reason === "behind-surface" ? text.askMissBehindSurface : text.askMissNotOnScreen;
    const message = [`${card.job} —`, line, card.prerequisites].filter(Boolean).join(" ");
    notify({ severity: reason === "behind-surface" ? "info" : "warning", message });
  };

  const handleNavigate = (card: AssistantResultCard): void => {
    cancelFlash.current?.();
    const activate = card.anchorAction === "open";
    // Start the flash BEFORE navigating, and only for a control the destination renders.
    if (card.anchor && canPointAt(card, pathname)) {
      cancelFlash.current = flashAnchor(card.anchor, {
        activate,
        surface: card.anchorSurface,
        onYield,
        // The drawer sits above the app's dialogs, so a form opened under it would be invisible.
        onHandover: onNavigated,
        onMissing: (reason) => reportMiss(card, reason),
      });
    }
    if (data?.queryId) {
      // An action, not a verdict — sent without `helpful`, and it never blocks the navigation.
      void sendAssistantFeedback({ queryId: data.queryId, deeplinkFollowed: true }).catch(
        () => undefined
      );
    }
    // Already where the control is: point at it here instead of navigating.
    if (card.anchor !== null && matchesRoute(card.anchorRoute, pathname)) {
      if (activate) onNavigated();
      return;
    }
    if (pathname !== card.deeplink) {
      navigate(card.deeplink);
      onNavigated();
    }
  };

  const handleFeedback = (helpful: boolean): void => {
    if (!data?.queryId) return;
    setFeedbackSent(true);
    void sendAssistantFeedback({ queryId: data.queryId, helpful }).catch(() => undefined);
  };

  return (
    <div className={styles.root}>
      <div className={styles.top}>
        <Input
          className={styles.input}
          size="small"
          autoFocus
          value={question}
          onChange={(_event, value) => setQuestion(value.value)}
          placeholder={text.askPlaceholder}
          aria-label={text.askPlaceholder}
          maxLength={300}
          contentBefore={<Search16Regular />}
          contentAfter={searching ? <Spinner size="extra-tiny" /> : undefined}
          data-help="help-ask-input"
        />
        <div className={styles.hint}>{text.askHint}</div>
      </div>

      <div className={styles.results} data-help="help-ask-results">
        {!enabled && <div className={styles.muted}>{text.askEmpty}</div>}
        {enabled && searching && !data && <div className={styles.muted}>{text.askThinking}</div>}

        {cards.map((card) => (
          <ResultCard key={card.id} card={card} text={text} onNavigate={handleNavigate} />
        ))}

        {data && !data.matchFound && (
          <div className={styles.notFound} data-help="help-ask-not-found">
            <div className={styles.notFoundText}>{data.notFound?.message}</div>
            {data.notFound?.suggestions && data.notFound.suggestions.length > 0 && (
              <>
                <div className={styles.tryThese}>{text.askTryThese}</div>
                <div className={styles.suggestions}>
                  {data.notFound.suggestions.map((suggestion) => (
                    <Button
                      key={suggestion}
                      size="small"
                      shape="circular"
                      appearance="outline"
                      onClick={() => setQuestion(suggestion)}
                    >
                      {suggestion}
                    </Button>
                  ))}
                </div>
              </>
            )}
            <div className={styles.chatbot}>{text.askChatbotHint}</div>
          </div>
        )}

        {data && cards.length > 0 && (
          <div className={styles.feedback}>
            <span>{feedbackSent ? text.askThanks : text.askHelpful}</span>
            {!feedbackSent && (
              <>
                <Tooltip content={text.askYes} relationship="label">
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<ThumbLike16Regular />}
                    onClick={() => handleFeedback(true)}
                  />
                </Tooltip>
                <Tooltip content={text.askNo} relationship="label">
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<ThumbDislike16Regular />}
                    onClick={() => handleFeedback(false)}
                  />
                </Tooltip>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
